import React, { useMemo } from "react"
import { Box, IconButton, Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome"
import { tailwindPalette } from "../../modules/theme/tailwindPalette"
import { useNavigate, useMatch } from "react-router-dom"
import { LocalizedStrict } from "../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../modules/localization/translations"
import { EnvironmentType } from "../../modules/environment/envTypes"
import { useEnvironment } from "../../modules/environment/envHooks"
import { whenPrintingPageOnly } from "../components/print/printPageOnly"

const useStyles = makeStyles()(() => ({
    sidebar: {
        zIndex: 3,
        padding: "1rem",
        position: "fixed",
        top: 0,
        left: "1rem",
        width: "4rem",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        ...whenPrintingPageOnly({ display: "none" }),
    },
    navItems: {
        padding: ".3rem",
        backgroundColor: "white",
        borderRadius: ".75rem",
        boxShadow: "0px 2px 12px 0px #0000001F",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: ".5rem",
    },
    iconButton: {
        backgroundColor: tailwindPalette.slate[200],
        width: "2.75rem",
        height: "2.75rem",
        borderRadius: "0.75rem",
        color: tailwindPalette.slate[600],
        "&:hover": {
            backgroundColor: tailwindPalette.blue[400],
            color: "white",
        },
    },
    activeIcon: {
        backgroundColor: tailwindPalette.blue[500],
        color: "white",
    },
}))

export type NarrowNavItem = {
    icon: FontAwesomeIconProps["icon"]
    label?: string
    translationLabel: TranslationKey
    route: string
    environments?: EnvironmentType[]
}

export type NarrowNavItemsProps = {
    navItems: Array<NarrowNavItem>
}

export function NarrowNavItems({ navItems }: NarrowNavItemsProps) {
    const { classes, cx } = useStyles()
    const { currentEnv } = useEnvironment()
    const navigate = useNavigate()

    const filteredItems = useMemo(() => {
        return navItems.filter((item) => item.environments?.includes(currentEnv.type) ?? true)
    }, [navItems])

    return (
        <Box className={classes.sidebar}>
            <Box className={classes.navItems}>
                {filteredItems.map((item) => {
                    const isActive = useMatch(item.route) !== null

                    return (
                        <React.Fragment key={item.translationLabel}>
                            <LocalizedStrict id={item.translationLabel} attrs={{ title: true }}>
                                <Tooltip placement="right-start" arrow title={item.label}>
                                    <IconButton
                                        className={cx(classes.iconButton, { [classes.activeIcon]: isActive })}
                                        onClick={() => navigate(item.route)}
                                    >
                                        <FontAwesomeIcon size="xs" icon={item.icon} />
                                    </IconButton>
                                </Tooltip>
                            </LocalizedStrict>
                        </React.Fragment>
                    )
                })}
            </Box>
        </Box>
    )
}
